import { FormItemsGroup, Input, Space } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchCompanies } from '@modules-deprecated/app/companies/hooks/useFetchCompanies'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { Scope } from '../../../../../../../enums/scope'
import { useFormContext, useSegment, useWatch } from '../../../../../../../hooks'
import { getImageClassName } from '../../../../../../../utils/getClassName'
import { getImgPath } from '../../../../../../../utils/getImgPath'
import { CompanySelect } from '../../../../../../selects/CompanySelect'
import { CountrySelect } from '../../../../../../selects/CountrySelect'
import { ContactFormSchema } from '../../../../utils/formData'
import { ContactStandardSettings } from '../../../ContactStandardSettings'
import { SelectedCompanyBox } from '../../../SelectedCompanyBox'
import * as Styled from './styles'

export const SupplierCompanyItems = (): ReactElement => {
  const { t } = useTranslation()
  const { FormItem, setValue, control } = useFormContext<ContactFormSchema>()
  const companyFromSearch = useWatch({ control, name: 'companyFromSearch' })
  const companyName = useWatch({ control, name: 'companyName' })
  const shouldShowDefaultState = !companyFromSearch?.id && !companyName?.length
  const { companies } = useFetchCompanies(companyFromSearch?.id)

  const { track } = useSegment()

  const handleCompanySelect = useCallback(
    (id?: string) => {
      if (id) {
        setValue('companyName', undefined)
      }
    },
    [setValue],
  )

  const handleCompanyCreate = useCallback(
    (name: string) => {
      setValue('companyName', name)
      setValue('companyFromSearch', undefined)
    },
    [setValue],
  )

  useEffect(() => {
    if (companies.length === 1) {
      setValue('companyFromSearch', companies[0])
    }
  }, [companies, setValue])

  return (
    <>
      <FormItem
        label={t('create_contact_modal.form.company')}
        name="companyFromSearch"
        render={({ field: { value, onChange, ...props } }) => (
          <CompanySelect
            autoFocus={!companyFromSearch?.id}
            allowCreate
            inputValue={companyName || getCompanyName(value) || ''}
            onCreate={handleCompanyCreate}
            onSelect={(id, value) => {
              onChange(value)
              handleCompanySelect(id)
              track(ExpensesTrackingEvents.SupplierCompanyCreatedManually)
            }}
            scopes={Scope.ContactRegisterLookup}
            selectedId={value?.id}
            selectedValue={value}
            {...props}
          />
        )}
      />

      {!!companyFromSearch?.id && <SelectedCompanyBox company={companyFromSearch} />}

      <Space size="s16" />

      {shouldShowDefaultState && (
        <Styled.ImageWrapper className={getImageClassName()}>
          <Space size="s8" />
          <img
            alt="Illustration-contact"
            height={193}
            loading="lazy"
            src={getImgPath('/modals/illustration-contact.svg')}
            width={272}
          />
        </Styled.ImageWrapper>
      )}

      {!!companyName && (
        <FormItemsGroup itemsInRow={2}>
          <FormItem
            label={t('create_contact_modal.form.tax_id')}
            name="taxId"
            render={({ field: { value, onChange, ...props } }) => (
              <Input
                {...props}
                onChange={onChange}
                placeholder={t('create_contact_modal.form.tax_id')}
                value={value}
                type="number"
              />
            )}
          />

          <FormItem
            label={t('create_contact_modal.form.country')}
            name="country"
            render={({ field: { value, onChange, ...props } }) => (
              <CountrySelect
                {...props}
                dropdownSize="fitTrigger"
                onSelect={(id, value) => onChange(value)}
                selectedId={value?.id}
              />
            )}
          />
        </FormItemsGroup>
      )}

      {!shouldShowDefaultState && (
        <>
          <Space size="s24" />
          <ContactStandardSettings />
        </>
      )}
    </>
  )
}
